import { useState } from "react";
import {
  Dialog,
  DialogFooter,
  DialogText,
  FailedCallout,
  ReasonDialog,
  ReasonField,
  TextField,
  isApiError,
  useCommandMutation,
  useReason,
  useToast,
} from "@amber/ui";
import type { DomainRow } from "../../contracts/index";
import { accessCommands } from "../queries";
import { AFTER_ACCESS_CHANGE } from "../keys";

export function AddDomainDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [domain, setDomain] = useState("");
  const mutation = useCommandMutation({
    run: (csrf, text: string) => accessCommands.addDomain(domain.trim(), text, csrf),
    invalidate: AFTER_ACCESS_CHANGE,
    onSuccess: () => {
      toast.show(`${domain.trim().toLowerCase()} added`);
      onClose();
    },
  });
  const fieldErrors = isApiError(mutation.error) ? mutation.error.fieldErrors() : {};
  const reason = useReason(fieldErrors["reason"]);
  const submit = () => {
    if (reason.submit() && domain.trim()) mutation.mutate(reason.trimmed);
  };
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Add an auto-join domain"
      onSubmit={submit}
      footer={
        <DialogFooter onClose={onClose} confirm="Add domain" pending={mutation.isPending} failed={mutation.isError} />
      }
    >
      <DialogText>Anyone signing in with a Google Workspace address from this domain joins as a Viewer.</DialogText>
      <TextField
        label="Domain"
        required
        placeholder="example.org"
        value={domain}
        onChange={(e) => {
          setDomain(e.target.value);
        }}
        error={
          fieldErrors["domain"] ?? (reason.submitted && !domain.trim() ? "Enter a domain such as example.org" : null)
        }
      />
      <ReasonField value={reason.reason} onChange={reason.setReason} error={reason.error} />
      <FailedCallout error={mutation.error} fieldsShown />
    </Dialog>
  );
}

export function RemoveDomainDialog({ domain, onClose }: { domain: DomainRow; onClose: () => void }) {
  return (
    <ReasonDialog
      title={`Remove ${domain.domain}?`}
      confirm="Remove"
      danger
      success={`${domain.domain} removed`}
      onClose={onClose}
      invalidate={AFTER_ACCESS_CHANGE}
      run={(reason, csrf) => accessCommands.removeDomain(domain.id, reason, csrf)}
    >
      New people from this domain can no longer join on their own. The{" "}
      {domain.people_joined === 1 ? "account" : `${String(domain.people_joined)} accounts`} of people who already joined
      stay as they are.
    </ReasonDialog>
  );
}
